import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import { getImpressoesPorEstado } from '../../../api/reportApi';
import ImpressionsByStateChart from '../ImpressionsByStateChart';
import TableContainer from '../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface StateMapCardProps {
  campaignId: string;
}

export interface StateMapCardRef {
  refresh: () => void;
}

const StateMapCard = forwardRef<StateMapCardRef, StateMapCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const stateData = await getImpressoesPorEstado(parseInt(campaignId));
        setData(stateData);
      } catch (err) {
        console.error('Error fetching state data:', err);
        setError('Erro ao carregar dados por estado');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Impressões por Estado</h4>
          </CardHeader>
          <CardBody>
            <div className="text-center text-danger">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          </CardBody>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Impressões por Estado</h4>
          </CardHeader>
          <CardBody>
            <div className="text-center text-muted">
              <i className="ri-information-line me-2"></i>
              Nenhum dado de estado disponível
            </div>
          </CardBody>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'Estado',
        accessorKey: 'state',
        cell: (row: any) => (
          <div className="fw-medium">{row.getValue() || 'Unknown'}</div>
        ),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Impressões',
        accessorKey: 'impression',
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Visíveis',
        accessorKey: 'viewable',
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Cliques',
        accessorKey: 'click',
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <h4 className={styles.cardTitle}>Impressões por Estado</h4>
        </CardHeader>
        <CardBody>
          <Row>
            <Col lg={6}>
              {data && <ImpressionsByStateChart series={data} />}
            </Col>
            <Col lg={6}>
              <TableContainer
                divClass="table-responsive"
                columns={columns}
                data={data}
                isGlobalFilter={false}
                customPageSize={10}
                initialSorting={[{ id: 'impression', desc: true }]}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  }
);

StateMapCard.displayName = 'StateMapCard';

export default StateMapCard;
